import { List } from '@chakra-ui/react';
import { IUsageEventRequestSchema } from '@monetize/types/app';
import { formatInteger, pluralize } from '@monetize/utils/core';
import { createColumnHelper } from '../../../../../components/Monetize/DataTableNew/appTable';
import { FunctionComponent as FC, useEffect, useMemo, useState } from 'react';
import {
  MAlert,
  MFlex,
  MTag,
  MTagCloseButton,
  MTagLabel,
  MText,
} from '../../../../../components/Monetize';
import { DataTable } from '../../../../../components/Monetize/DataTableNew/DataTable';
import {
  AccountLookupPopover,
  SubscriptionLookupPopover,
  UsageTypeLookupPopover,
} from '../../../../../components/Monetize/LookupPopover';
import { useUsageEventCustomFields } from '../../../../../hooks/useUsageEventCustomFields';
import { useFlags } from '../../../../../services/launchDarkly';
import { collectPropertyKeys } from '../usageProperties.utils';
import { getUsagePropertyColumns } from '../usagePropertyColumns';

const MAX_VISIBLE_ROWS = 1000;

const columnHelper = createColumnHelper<IUsageEventRequestSchema>();

const baseColumns = [
  columnHelper.accessor('id', {
    cell: (info) => info.getValue(),
    size: 220,
    minSize: 120,
    enableSorting: false,
  }),
  columnHelper.accessor('timestamp', {
    cell: (info) => info.getValue(),
    size: 210,
    minSize: 150,
    enableSorting: false,
  }),
  columnHelper.accessor('accountId', {
    cell: (info) => <AccountLookupPopover value={info.getValue()} />,
    size: 180,
    minSize: 180,
    enableSorting: false,
  }),
  columnHelper.accessor('subscriptionId', {
    cell: (info) => (
      <SubscriptionLookupPopover
        value={info.getValue()}
        record={info.row.original}
      />
    ),
    size: 180,
    minSize: 180,
    enableSorting: false,
  }),
  columnHelper.accessor('usageTypeId', {
    cell: (info) => <UsageTypeLookupPopover value={info.getValue()} />,
    size: 180,
    minSize: 180,
    enableSorting: false,
  }),
  columnHelper.accessor('unitsConsumed', {
    cell: (info) => info.getValue(),
    size: 150,
    minSize: 120,
    enableSorting: false,
  }),
  columnHelper.accessor('ratedPrice', {
    cell: (info) => info.getValue(),
    enableResizing: false,
    enableSorting: false,
  }),
];

function getRowCountText(rowCount: number): string {
  if (rowCount > MAX_VISIBLE_ROWS) {
    return `Previewing the first ${formatInteger(MAX_VISIBLE_ROWS)} of ${formatInteger(
      rowCount,
    )} rows`;
  }
  return `${formatInteger(rowCount)} ${pluralize('row', rowCount)}`;
}

interface UploadUsageFileSummaryProps {
  filename: string;
  rows: IUsageEventRequestSchema[];
  warnings?: string[];
  onRemoveFile: () => void;
}

export const UploadUsageFileSummary: FC<UploadUsageFileSummaryProps> = ({
  filename,
  rows,
  warnings,
  onRemoveFile,
}: UploadUsageFileSummaryProps) => {
  const [visibleRows, setVisibleRows] = useState(() =>
    rows.slice(0, MAX_VISIBLE_ROWS),
  );
  const { enableUsageCustomFields } = useFlags();
  const { customFieldsByKey } = useUsageEventCustomFields();

  useEffect(() => {
    setVisibleRows(rows.slice(0, MAX_VISIBLE_ROWS));
  }, [rows]);

  // Keys come from the previewed rows rather than the configured field list:
  // this table is a readback of the file, so an empty column for a field the
  // CSV left out would only be noise.
  const propertyKeys = useMemo(
    () => (enableUsageCustomFields ? collectPropertyKeys(visibleRows) : []),
    [enableUsageCustomFields, visibleRows],
  );
  const propertyKeysSignature = propertyKeys.join('|');

  const columns = useMemo(
    () => [
      ...baseColumns,
      ...getUsagePropertyColumns(columnHelper, propertyKeys, customFieldsByKey),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [propertyKeysSignature, customFieldsByKey],
  );

  return (
    <>
      {warnings && warnings.length > 0 && (
        <MAlert type="warning" mb={4}>
          The following columns were not recognized and will be ignored:
          {/* A bare `<ul>` shows no bullets: v3's preflight sets
              `list-style: none` on every list. */}
          <List.Root as="ul">
            {warnings.map((warning) => (
              <List.Item key={warning}>{warning}</List.Item>
            ))}
          </List.Root>
        </MAlert>
      )}
      <MFlex alignItems="center" mb={2}>
        <MText fontWeight="600" mr={2}>
          File:
        </MText>
        <MTag size="md" variant="upload">
          <MTagLabel>{filename}</MTagLabel>

          <MTagCloseButton
            data-testid="clear-item-btn"
            onClick={() => onRemoveFile()}
          />
        </MTag>
      </MFlex>
      <MText mb={2}>{getRowCountText(rows.length)}</MText>
      <DataTable
        enablePagination={false}
        columns={columns}
        data={visibleRows}
        clientControlled
        skipFilterValuePreprocessing
      />
    </>
  );
};
